import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { topicLabel, VOICE_POSITIONS, voiceLabel } from '@gogrov/shared'
import { useStory, useRegenerate, useStoryVersions, useBuildVoice, useBuildStory, STAGE_PERCENT, type BuildStage } from '@/hooks/useHeadlines'
import { useTopics } from '@/hooks/useConfig'
import { SEED_COSTS, useSeedBalance } from '@/hooks/useSeeds'
import { useProfile } from '@/hooks/useProfile'
import { useEffectiveClient } from '@/hooks/useAuth'
import { useStoryOrHeadline, useStoryProgress, useSendToPhone } from '@/hooks/useLibrary'
import { useAddToNewsletter, useNewsletterItems, useRemoveFromNewsletter } from '@/hooks/useNewsletterItems'
import type { Headline, Story, Voice } from '@/types/database'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { SeedCost } from '@/components/gg/SeedChip'
import { VoiceSegments, VoiceInfo, REPORTER_NOTE } from '@/components/gg/Voice'
import { StoryPicture } from '@/components/story/StoryPicture'
import { useRecordProv } from '@/hooks/useFeed'
import { tossSeeds, useSeedFx } from '@/lib/seedFx'
import { noDashes } from '@/lib/text'
import '@/styles/story2.css'

/**
 * A story (Brian's after/article.html v2.6, with the Oct 22 desktop round).
 *
 * The picture across the top; the topic, the headline and the source line
 * (Public record, or how many sources carry it, the outlet, its age); a
 * warning on a story older than a week. Then What's been reported (What's on
 * the record for a public record), the Script in its position (written one at
 * a time: another position is 1 seed, going back to one is free) and the
 * Caption with ＋ Newsletter.
 *
 * A feed card that was never built opens here as a preview: what's been
 * reported, and Build this story · 12 in place of the script. On the desktop
 * the story goes to the phone (Send to phone) instead of Record this.
 */

const NO_SEEDS = 'Not enough seeds left for that. This week’s come back Monday.'
const DAY = 864e5

function CopyButton({ text, label, onCopied }: { text: string | null; label: string; onCopied: (msg: string) => void }) {
  async function copy() {
    if (!text) return
    try { await navigator.clipboard.writeText(text); onCopied(`${label[0].toUpperCase()}${label.slice(1)} copied.`) }
    catch { onCopied('Copy did not work here. Select the text instead.') }
  }
  return (
    <button type="button" className="story3__ic" onClick={copy} disabled={!text} aria-label={`Copy ${label}`}>
      <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>Copy
    </button>
  )
}

/** Marks the first keyword hit in a line of text. */
function Marked({ text, term }: { text: string; term: string | null }) {
  if (!term) return <>{text}</>
  const i = text.toLowerCase().indexOf(term.toLowerCase())
  if (i < 0) return <>{text}</>
  return <>{text.slice(0, i)}<mark className="story3__kw">{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>
}

function Paragraphs({ text, term = null }: { text: string; term?: string | null }) {
  return <>{noDashes(text).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean).map((p, i) => <p key={i}><Marked text={p} term={term} /></p>)}</>
}

/** Hashtags are the only pills (Brian, Oct 21). */
function Caption({ text }: { text: string }) {
  return (
    <>
      {noDashes(text).split(/\n{2,}/).map((para, i) => {
        const parts = para.split(/(#[\p{L}\p{N}_]+)/u)
        return (
          <p key={i}>
            {parts.map((x, j) => x.startsWith('#') ? <span key={j} className="story3__hash">{x}</span> : <Fragment key={j}>{x}</Fragment>)}
          </p>
        )
      })}
    </>
  )
}

function ageOf(iso: string | null | undefined): { days: number; label: string } {
  const t = iso ? Date.parse(iso) : NaN
  if (isNaN(t)) return { days: 0, label: '' }
  const ms = Math.max(0, Date.now() - t), h = Math.floor(ms / 3.6e6), d = Math.floor(ms / DAY)
  return { days: d, label: h < 1 ? 'Just now' : h < 24 ? `${h}h` : d < 14 ? `${d}d` : `${Math.round(d / 7)}w` }
}

/** The source count, when the pipeline has supplied one. */
function sourceCount(...rows: (object | null | undefined)[]): number {
  for (const r of rows) {
    const n = (r as { source_count?: number | null } | null | undefined)?.source_count
    if (typeof n === 'number' && n > 0) return n
  }
  return 0
}

interface Toast { text: ReactNode; ms?: number }

export function StoryView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  const resolved = useStoryOrHeadline(id)
  const kind = resolved.data?.kind
  const storyId = kind === 'story' ? resolved.data!.story!.id : undefined

  // The built story is read through useStory, so Regenerate and another
  // position refresh it the way they always have.
  const { data: liveStory } = useStory(storyId)
  const story: Story | null = liveStory ?? (kind === 'story' ? resolved.data!.story : null)
  const headline: Headline | null = resolved.data?.headline ?? null

  // A headline id that was built since: show it at its story's address.
  useEffect(() => {
    if (kind === 'story' && storyId && id !== storyId) navigate(ROUTES.story(storyId), { replace: true })
  }, [kind, storyId, id, navigate])

  const [toast, setToast] = useState<Toast | null>(null)
  const toastT = useRef(0)
  const say = (text: ReactNode, ms = 2600) => {
    setToast({ text, ms }); window.clearTimeout(toastT.current)
    toastT.current = window.setTimeout(() => setToast(null), ms)
  }
  useEffect(() => () => window.clearTimeout(toastT.current), [])

  if (resolved.isLoading || (!resolved.data && !resolved.isError)) {
    return <><PageChrome title="Story" navKey="feed" back /><State loading /></>
  }
  if (resolved.isError || kind === 'missing' || (!story && !headline)) {
    return <><PageChrome title="Story" navKey="feed" back /><State error={resolved.isError} /></>
  }

  return story
    ? <BuiltStory key={story.id} story={story} headline={headline} desktop={desktop} say={say} toast={toast} />
    : <PreviewStory key={headline!.id} headline={headline!} desktop={desktop} say={say} toast={toast} />
}

function State({ loading, error }: { loading?: boolean; error?: boolean }) {
  const navigate = useNavigate()
  return (
    <div className="gg-screen"><div className="gg-screen__body"><div className="gg-column story2__state">
      {loading ? <><span className="gg-spinner" /><p>Opening the story.</p></> : (
        <>
          <h2>{error ? 'Could not load this story' : 'Story not found'}</h2>
          <p>{error ? 'The connection dropped or the story is unavailable.' : 'It may have been removed, or the link is wrong.'}</p>
          <button className="gg-btn" onClick={() => navigate(ROUTES.library)}>Go to your Library</button>
        </>
      )}
    </div></div></div>
  )
}

/** The head every story shares: topic, headline, source line, picture. */
function StoryHead({ topic, title, sourceName, sourceUrl, record, published, sources, term, imageUrl, imageCredit, headlineId }: {
  topic: string; title: string; sourceName: string | null; sourceUrl: string | null; record: boolean
  published: string | null | undefined; sources: number; term: string | null
  imageUrl: string | null | undefined; imageCredit: string | null | undefined; headlineId?: string | null
}) {
  const client = useEffectiveClient()
  const { data: prov } = useRecordProv(headlineId, record)
  const age = ageOf(published)
  // Brian v2.1: Public record, or how many sources carry it; older than a week says so.
  const cls = record ? 'Public record' : sources > 1 ? `${sources} sources` : 'Reported'
  const old = !record && age.days > 7
  return (
    <>
      <span className="story3__pill"><TopicGlyph topic={topic} size={25} />{topicLabel(topic)}</span>
      <h1 className="story3__h1"><Marked text={noDashes(title)} term={term} /></h1>
      <div className="story3__src">
        <span className="story3__sl">
          {cls} · {sourceUrl
            ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer">{sourceName || 'the source'}</a>
            : <u>{sourceName || 'Source unknown'}</u>}
          {age.label && <> · {age.label}</>}
          {client?.display_name && <span className="story3__by"> · {client.display_name}</span>}
        </span>
        {term && <span className="story3__hit">KEYWORD HIT</span>}
        {old && (
          <span className="story3__old">
            From {age.days >= 14 ? `${Math.round(age.days / 7)} weeks` : `${age.days} days`} ago. Check it’s still current before you post.
          </span>
        )}
      </div>
      <StoryPicture imageUrl={imageUrl} credit={imageCredit} topic={topic} ratio="16/10" radius={16} className="story3__hero" seedKey={headlineId ?? title}
        story={{ id: headlineId ?? title, topic, headline: title }} />
      {record && prov ? (
        <div className="story3__prov4">
          <div><span className="story3__provk">Record system</span><span className="story3__provv">{prov.system}</span></div>
          <div><span className="story3__provk">Document</span><span className="story3__provv">{prov.doc}</span></div>
          <div><span className="story3__provk">Coverage check</span><span className="story3__provv">{prov.check}</span></div>
          <div><span className="story3__provk">Sources</span><span className="story3__provv"><span className="story3__seal">Public record</span></span></div>
        </div>
      ) : (record || sources > 1) && (
        <div className="story3__prov">
          <span className="story3__provk">Sources</span>
          {record ? <span className="story3__seal">Public record</span> : <span className="story3__provv">Checked · {sources} sources</span>}
        </div>
      )}
    </>
  )
}

function ReportedBlock({ text, record, term, children }: { text: string | null; record: boolean; term: string | null; children?: ReactNode }) {
  return (
    <section className="story2__block story3__blk">
      <div className="story2__head">
        <span className="story3__k" title={record
          ? 'Plain facts from the public record, linked and dated. The script retells it for your audience.'
          : 'A summary of what the outlets reported, attributed. The script retells it for your audience.'}>
          {record ? 'What’s on the record' : 'What’s been reported'}
        </span>
        <div className="story2__tools">{children}</div>
      </div>
      <div className="story2__text">
        {text ? <Paragraphs text={text} term={term} /> : <p className="gg-help">Nothing summarized yet.</p>}
      </div>
    </section>
  )
}

function ToastView({ toast }: { toast: Toast | null }) {
  return <div className={`story3__toast${toast ? ' is-on' : ''}`} role="status">{toast?.text}</div>
}

// ---------------------------------------------------------------- preview

function PreviewStory({ headline, desktop, say, toast }: {
  headline: Headline; desktop: boolean; say: (t: ReactNode, ms?: number) => void; toast: Toast | null
}) {
  const navigate = useNavigate()
  const build = useBuildStory()
  const { data: topics } = useTopics()
  const { data: balance } = useSeedBalance()
  const releaseHold = useSeedFx((s) => s.release)
  const [stage, setStage] = useState<BuildStage | null>(null)
  const voice: Voice = (topics?.find((t) => t.topic === headline.topic)?.voice as Voice | undefined) ?? 'newcomer'
  const topic = headline.topic ?? 'civic-government'
  const term = (headline.keyword_hits ?? [])[0] ?? null
  const busy = build.isPending

  async function doBuild() {
    if (busy) return
    if ((balance?.total_available ?? 0) < SEED_COSTS.build_story) { say(<><b>Not enough seeds left for that.</b> This week’s come back Monday.</>, 3400); return }
    tossSeeds(SEED_COSTS.build_story)
    try {
      const { story } = await build.mutateAsync({ headlineId: headline.id, onStage: setStage })
      navigate(ROUTES.story(story.id), { replace: true })
      say('Built. Your script is ready.')
    } catch (e) {
      releaseHold(); setStage(null)
      const err = e as Error
      say(err.name === 'InsufficientSeedsError' ? NO_SEEDS : err.message || 'That build did not go through. Try it again.', 3400)
    }
  }

  const label = busy ? `Writing${stage ? ` · ${STAGE_PERCENT[stage]}%` : ''}` : <>Build this story<SeedCost n={SEED_COSTS.build_story} /></>
  return (
    <>
      <PageChrome title="Story" navKey="feed" back
        actions={desktop ? <button type="button" className="gg-act gg-act--amber" disabled={busy} onClick={() => void doBuild()}>{label}</button> : undefined} />
      <section className="gg-screen story3">
        <div className="gg-screen__body">
          <article className="gg-column story2">
            <StoryHead topic={topic} title={headline.headline} sourceName={headline.source_name} sourceUrl={headline.source_url}
              record={headline.provenance === 'record'} published={headline.source_published_at ?? headline.fetched_at}
              sources={sourceCount(headline)} term={term} imageUrl={headline.image_url} imageCredit={headline.image_credit} headlineId={headline.id} />
            <ReportedBlock text={headline.summary} record={headline.provenance === 'record'} term={term} />
            <section className="story2__block story3__gate">
              <div className="story2__head"><span className="story3__k">Your script</span></div>
              <p>Build writes the script and caption in your voice, as {voiceLabel(voice)}, from what&apos;s been reported above.</p>
              <button type="button" className="story3__buildbtn" disabled={busy} onClick={() => void doBuild()}>
                {busy ? label : 'Build this story'}
              </button>
              {busy && <div className="story3__bar"><i style={{ width: `${stage ? STAGE_PERCENT[stage] : 4}%` }} /></div>}
            </section>
          </article>
        </div>
        {!desktop && (
          <div className="gg-bar story3__actionbar">
            <div className="gg-bar__inner">
              <button className="gg-stack-btn gg-stack-btn--amber story3__go" disabled={busy} onClick={() => void doBuild()}>{label}</button>
            </div>
          </div>
        )}
        <ToastView toast={toast} />
      </section>
    </>
  )
}

// ---------------------------------------------------------------- built

function BuiltStory({ story, headline, desktop, say, toast }: {
  story: Story; headline: Headline | null; desktop: boolean; say: (t: ReactNode, ms?: number) => void; toast: Toast | null
}) {
  const navigate = useNavigate()
  const id = story.id
  const { data: versions } = useStoryVersions(id)
  const { data: topics } = useTopics()
  const { data: profile } = useProfile()
  const { data: balance } = useSeedBalance()
  const { data: progress } = useStoryProgress(id)
  const regenerate = useRegenerate()
  const buildVoice = useBuildVoice()
  const sendToPhone = useSendToPhone()
  const nl = useNewsletterItems()
  const addNl = useAddToNewsletter()
  const removeNl = useRemoveFromNewsletter()
  const releaseHold = useSeedFx((s) => s.release)

  const [pending, setPending] = useState<'article' | 'script' | 'voice' | null>(null)
  const [override, setOverride] = useState(false)
  const [chosen, setChosen] = useState<Voice>('newcomer')

  const topicDefault: Voice = (topics?.find((t) => t.topic === story.category)?.voice as Voice | undefined) ?? 'newcomer'
  const current: Voice = (story.voice as Voice | undefined) ?? 'newcomer'
  const owned = useMemo(() => new Set((versions ?? []).map((v) => v.voice)), [versions])
  const reminders = profile?.voice_reminders ?? true
  const record = story.provenance === 'record'
  const term = (story.keyword_hits ?? [])[0] ?? null

  // Open with the override on when the story shows something other than the
  // topic default, so the row tells the truth about what is on screen.
  useEffect(() => { setOverride(current !== topicDefault); setChosen(current) }, [id, topics]) // eslint-disable-line react-hooks/exhaustive-deps

  const effective: Voice = override ? chosen : topicDefault
  const needsVoice = effective !== current
  const voiceOwned = owned.has(effective)
  const available = balance?.total_available ?? 0

  async function run(target: 'article' | 'script') {
    if (pending) return
    if (available < SEED_COSTS.regenerate) { say(NO_SEEDS, 3400); return }
    setPending(target)
    tossSeeds(SEED_COSTS.regenerate)
    try { await regenerate.mutateAsync({ storyId: id, target }) }
    catch (e) { releaseHold(); say((e as Error).message || 'That rewrite did not go through. You were not charged.', 3400) }
    finally { setPending(null) }
  }

  async function rebuild() {
    if (pending) return
    if (!needsVoice) return run('script')
    if (!voiceOwned && available < SEED_COSTS.voice_build) { say(NO_SEEDS, 3400); return }
    setPending('voice')
    if (!voiceOwned) tossSeeds(SEED_COSTS.voice_build)
    try {
      await buildVoice.mutateAsync({ storyId: id, voice: effective })
      if (voiceOwned) say(`Back to ${voiceLabel(effective)}. Already written, so nothing used.`)
    } catch (e) { releaseHold(); say((e as Error).message || 'That did not go through. You were not charged.', 3400) }
    finally { setPending(null) }
  }

  // ＋ Newsletter (Brian v2.2): the AI writes the newsletter version first, then it joins this week's issue.
  const nlItem = nl.find({ storyId: id, headlineId: story.headline_id })
  async function toggleNewsletter() {
    if (addNl.isPending || removeNl.isPending) return
    if (nlItem) {
      await removeNl.mutateAsync(nlItem.id).catch(() => null)
      say('Taken out of this week’s newsletter.')
      return
    }
    if (available < SEED_COSTS.newsletter_version) { say(NO_SEEDS, 3400); return }
    try {
      const { count } = await addNl.mutateAsync({ storyId: id })
      say(<>In this week’s newsletter · {count} {count === 1 ? 'story' : 'stories'}. <Link to={ROUTES.newsletter}>{desktop ? 'See the issue' : 'See this week’s issue'}</Link></>, 3400)
    } catch (e) {
      const err = e as Error & { status?: number }
      say(err.status === 402 ? NO_SEEDS : err.message || 'The newsletter version did not go through. You were not charged.', 3400)
    }
  }

  // Desktop (Oct 22, Dana: "Record this, on a computer that can't record"): send it to the phone.
  async function send() {
    try {
      await sendToPhone.mutateAsync({ storyId: id })
      say(<><b>Sent to your phone.</b> Open GoGrov there: it’s in Record ＋, script ready.</>, 3600)
    } catch { say('Could not send it to your phone. Try again.', 3400) }
  }

  // The original is the first version written; the rebuild, the latest other one (article.html: Show the original / Show the rebuild).
  const original: Voice | null = (versions?.[0]?.voice as Voice | undefined) ?? null
  const rebuilt: Voice | null = (() => {
    if (current !== original) return current
    const others = (versions ?? []).filter((v) => v.voice !== original)
    return (others[others.length - 1]?.voice as Voice | undefined) ?? null
  })()
  async function show(v: Voice) {
    if (pending || v === current) return
    setPending('voice')
    try { await buildVoice.mutateAsync({ storyId: id, voice: v }) }
    catch (e) { say((e as Error).message || 'That did not go through. You were not charged.', 3400) }
    finally { setPending(null) }
  }

  const pos = VOICE_POSITIONS.find((p) => p.id === current)!
  const sent = !!progress?.to_phone_at && !progress?.recorded_at
  const rebuildLabel = pending === 'voice' ? 'Writing' : pending === 'script' ? 'Rewriting'
    : !needsVoice ? <>Regenerate<SeedCost n={SEED_COSTS.regenerate} /></>
    : <>Rebuild as {voiceLabel(effective)}{!voiceOwned && <SeedCost n={SEED_COSTS.voice_build} />}</>

  const primary = desktop
    ? <button type="button" className="gg-act gg-act--amber" disabled={sendToPhone.isPending} onClick={() => void send()}>{sent ? 'Sent to phone' : 'Send to phone'}</button>
    : null

  return (
    <>
      <PageChrome title="Story" navKey="feed" back actions={primary ?? undefined} />
      <section className="gg-screen story3">
        <div className="gg-screen__body">
          <article className="gg-column story2">
            <StoryHead topic={story.category} title={story.headline} sourceName={story.source_name} sourceUrl={story.source_url}
              record={record} published={headline?.source_published_at ?? headline?.fetched_at ?? story.created_at}
              sources={sourceCount(story, headline)} term={term} imageUrl={story.image_url ?? headline?.image_url} imageCredit={story.image_credit ?? headline?.image_credit} headlineId={story.headline_id} />

            <ReportedBlock text={story.article ?? story.summary} record={record} term={term}>
              <CopyButton text={story.article ?? story.summary} label={record ? 'the record' : 'what’s been reported'} onCopied={say} />
              <button type="button" className="story3__ic" disabled={!!pending} onClick={() => void run('article')}>
                {pending === 'article' ? 'Rewriting' : <>Regenerate<SeedCost n={SEED_COSTS.regenerate} /></>}
              </button>
            </ReportedBlock>

            <section className={`story2__block story2__block--script story3__blk${pending === 'script' || pending === 'voice' ? ' is-pending' : ''}`}>
              <div className="story2__head">
                <span className="story3__k">Script</span>
                <div className="story2__tools"><CopyButton text={story.script} label="script" onCopied={say} /></div>
              </div>

              <div className="voice-row">
                <div className="voice-row__top">
                  <div>
                    <span className="voice-row__eyebrow story3__veb">Voice for {topicLabel(story.category)} <VoiceInfo show={reminders} /></span>
                    <strong className="voice-row__name">{override ? voiceLabel(chosen) : `${voiceLabel(topicDefault)} (default)`}</strong>
                    <span className={`voice-row__meta story3__vmeta${override ? ' is-on' : ''}`}>
                      {override ? 'override for this article' : 'topic default from Settings'}
                    </span>
                  </div>
                  <button type="button" role="switch" aria-checked={override} className="gg-switch gg-switch--lg"
                    aria-label="Override the voice for this article"
                    onClick={() => { setOverride((o) => !o); if (!override) setChosen(topicDefault) }} />
                </div>
                {override && (
                  <div className="voice-row__control">
                    <div className="voice-row__scale story3__scale"><span>How much authority you claim</span><span>low → high</span></div>
                    <VoiceSegments value={chosen} onChange={setChosen} large owned={owned} />
                    {chosen === 'reporter' && <div className="voice-row__reporter">{REPORTER_NOTE}</div>}
                  </div>
                )}
              </div>

              <div className="story2__text story2__text--script"><Paragraphs text={story.script} /></div>
              <p className="story3__closing">Closing · <b>{pos.closing}</b></p>

              <button className="story2__rebuild" disabled={!!pending} onClick={() => void rebuild()}>
                <svg className={pending === 'script' || pending === 'voice' ? 'is-spinning' : ''} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 3v6h-6" />
                </svg>
                {rebuildLabel}
              </button>
              <p className="story3__keep">
                {!original || owned.size < 2 || !rebuilt
                  ? 'Rebuilding keeps the original. Both versions hang off this article.'
                  : current === original
                    ? <>Showing the original ({voiceLabel(original)}). <button type="button" disabled={!!pending} onClick={() => void show(rebuilt)}>Show the rebuild</button></>
                    : <>Rebuilt as {voiceLabel(current)}. The original is kept. <button type="button" disabled={!!pending} onClick={() => void show(original)}>Show the original</button></>}
              </p>
            </section>

            <section className="story2__block story3__blk">
              <div className="story2__head">
                <span className="story3__k">Caption</span>
                <div className="story2__tools">
                  <button type="button" className={`story3__ic story3__nl${nlItem ? ' is-on' : ''}`} disabled={addNl.isPending || removeNl.isPending} onClick={() => void toggleNewsletter()}>
                    {addNl.isPending ? 'Writing…' : nlItem ? '✓ In newsletter' : <>＋ Newsletter<SeedCost n={SEED_COSTS.newsletter_version} /></>}
                  </button>
                  <CopyButton text={story.caption} label="caption" onCopied={say} />
                </div>
              </div>
              <div className="story3__caption">{story.caption ? <Caption text={story.caption} /> : <p className="gg-help">No caption yet.</p>}</div>
            </section>
          </article>
        </div>
        {!desktop && (
          <div className="gg-bar story3__actionbar">
            <div className="gg-bar__inner">
              <button className="gg-stack-btn gg-stack-btn--amber story3__go" onClick={() => navigate(`${ROUTES.record}?story=${id}`)}>Record this</button>
            </div>
          </div>
        )}
        <ToastView toast={toast} />
      </section>
    </>
  )
}

export default StoryView
